import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const spinKf = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.18); opacity: 0.5; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
`;

const eyebrow = css`
  ${mono};
  font-size: 0.62em;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut},
              transform ${tokens.durBase} ${tokens.easeOut},
              box-shadow ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  animation: ${fadeInUp} ${tokens.durSlow} ${tokens.easeOut};

  /* Mesh background sutil (igual files) */
  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  .spin { animation: ${spinKf} 1s linear infinite; }

  /* ============================================================
   * HEADER
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "NFE DOWNLOADER · MONITOR";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          padding: 0;
          background: transparent;
          border: none;
          border-radius: 0;
          font-weight: 400;
          opacity: 1;
          color: ${({ theme }) => theme.text};
          ${mono};
          font-size: 0.95em;
          opacity: 0.5;
          text-transform: uppercase;
          letter-spacing: 0.06em;

          &.active {
            color: ${tokens.brand};
            opacity: 1;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            font-weight: 500;

            &::before {
              content: "";
              width: 7px;
              height: 7px;
              border-radius: 50%;
              background: ${tokens.brand};
              box-shadow: 0 0 8px ${tokens.brandGlow};
              animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
            }
          }
        }

        .update-tag {
          padding: 0;
          background: transparent;
          border: none;
          opacity: 0.4;
          color: ${({ theme }) => theme.text};
          ${mono};
          font-size: 0.9em;

          &::before {
            content: "· ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS
   * ============================================================ */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin: 0 0 18px 0;

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 600px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    /* Variantes neutralizadas: sem border-left coloridas */
    &.primary, &.success, &.error, &.warning {
      border-left-width: 1px;
    }

    /* Erros vermelhos quando há erros */
    &.error .text .kpi-value {
      color: ${({ $hasErrors }) => $hasErrors ? tokens.danger : "inherit"};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .kpi-label {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: -0.005em;
          text-transform: none;
          line-height: 1.2;
          order: 2;
          margin-top: 2px;
        }

        .kpi-value {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
          order: 1;
        }
      }
    }

    /* Card "primary" (com is-complete) ganha border verde sutil */
    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.7; }

    .kpi-progress {
      height: 2px;
      background: ${({ theme }) => surface(theme, 2)};
      border-radius: ${tokens.radiusPill};
      margin-top: 12px;
      overflow: hidden;

      .kpi-progress-fill {
        height: 100%;
        border-radius: ${tokens.radiusPill};
        background: ${tokens.brand};
        transition: width 0.5s ${tokens.easeOut};
      }
    }

    /* Variante error: progress fill em danger se houver erros */
    &.error .kpi-progress-fill { background: ${tokens.danger}; }
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin: 0 0 18px 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .toolbar-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 9px 18px;
      background: ${tokens.brand};
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      color: #fff;
      font-family: ${tokens.fontSans};
      font-size: 0.82em;
      font-weight: 500;
      cursor: pointer;
      letter-spacing: -0.005em;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }

      &:active { transform: translateY(0); }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      svg { font-size: 1em; flex-shrink: 0; }
    }

    .search-box {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 14px;
      background: ${({ theme }) => surface(theme, 0)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      min-width: 180px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within {
        border-color: ${tokens.brandRing};
      }

      svg {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.95em;
      }

      input {
        background: transparent;
        border: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        outline: none;
        flex: 1;
        width: 100%;
        letter-spacing: -0.005em;

        &::placeholder {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }
      }
    }
  }

  /* ============================================================
   * MAIN LAYOUT (sidebar + grid)
   * ============================================================ */
  .main-layout {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 18px;
    align-items: start;

    @media (max-width: 1100px) { grid-template-columns: 1fr; }
  }

  .downloads-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;

    > * {
      width: calc(25% - 9px);
      min-width: 0;
    }

    @media (max-width: 1400px) {
      > * { width: calc(33.333% - 8px); }
    }
    @media (max-width: 900px) {
      > * { width: calc(50% - 6px); }
    }
    @media (max-width: 600px) {
      > * { width: 100%; }
    }
  }

  /* ============================================================
   * DRAG & DROP
   * ============================================================ */
  .sortable-wrapper {
    touch-action: none;
  }

  .sortable-wrapper.is-draggable {
    cursor: grab;
    &:active { cursor: grabbing; }
  }

  .sortable-wrapper.is-dragging .download-card {
    opacity: 0.35;
    border: 1px dashed ${tokens.brandRing};
    background: ${({ theme }) => surface(theme, 0)};
    box-shadow: none;
  }

  .download-card.drag-source { opacity: 0.35; }

  .download-card.drag-overlay-card {
    box-shadow: ${tokens.shadowLg};
    border: 1px solid ${tokens.brandRing};
    transform: rotate(1deg);
    background: ${({ theme }) => theme.card};
    border-radius: ${tokens.radiusLg};
    cursor: grabbing;
  }

  .downloads-grid.is-sorting > *:not(.new-request-card) {
    transition: transform 200ms ${tokens.easeOut};
  }

  /* ============================================================
   * MONITOR SIDEBAR
   * ============================================================ */
  .monitor-sidebar {
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: sticky;
    top: 20px;

    @media (max-width: 1100px) { position: static; }
  }

  /* ============================================================
   * MONITOR TABS — segmented control
   * ============================================================ */
  .monitor-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
  }

  .monitor-tab {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 14px;
    border: none;
    border-radius: ${tokens.radiusSm};
    background: transparent;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    font-family: ${tokens.fontSans};
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    letter-spacing: -0.005em;

    &:hover {
      opacity: 0.85;
      background: ${({ theme }) => surface(theme, 1)};
    }

    &.active {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 2)};
      color: ${tokens.brand};
      box-shadow: inset 0 0 0 1px ${({ theme }) => hairline(theme, true)};
    }

    svg { flex-shrink: 0; font-size: 0.95em; opacity: 0.85; }
  }

  /* ============================================================
   * MONITOR CARD
   * ============================================================ */
  .monitor-card {
    ${({ theme }) => cardBase(theme)};
    overflow: hidden;

    &.general { }

    &.node-card-v2 {
      &.empty { opacity: 0.7; }
      &.clickable {
        cursor: pointer;
        &:hover {
          border-color: ${({ theme }) => hairline(theme, true)};
          background: ${({ theme }) => surface(theme, 1)};
        }
      }
    }
  }

  .monitor-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px 8px;
    ${eyebrow};
    opacity: 0.5;

    svg { flex-shrink: 0; opacity: 0.7; }

    /* Título principal do card */
    > span {
      flex: 1;
      letter-spacing: 0.08em;
    }

    .node-title {
      ${mono};
      font-size: 1em;
      letter-spacing: 0.02em;
      text-transform: none;
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
    }

    /* hairline lateral pra dar leveza */
    &::after {
      content: "";
      flex: 0;
    }
  }

  .live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.text};
    opacity: 0.3;
    flex-shrink: 0;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &.active {
      background: ${tokens.brand};
      opacity: 1;
      box-shadow: 0 0 8px ${tokens.brandGlow};
      animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
    }

    &.downloading {
      background: ${tokens.brand};
      opacity: 1;
      box-shadow: 0 0 8px ${tokens.brandGlow};
      animation: ${pulseDot} 1.2s ${tokens.easeInOut} infinite;
    }

    &.idle {
      background: ${tokens.warn};
      opacity: 0.7;
    }
  }

  /* ============================================================
   * CURRENT COMPANY (header do progresso)
   * ============================================================ */
  .current-company {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 16px 12px;
    margin: 0;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  }

  .current-company-thumb {
    width: 32px;
    height: 32px;
    border-radius: ${tokens.radiusMd};
    background: ${tokens.brandSoft};
    color: ${tokens.brand};
    border: 1px solid ${tokens.brandRing};
    display: flex;
    align-items: center;
    justify-content: center;
    ${mono};
    font-size: 0.85em;
    font-weight: 600;
    flex-shrink: 0;
  }

  .current-company-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .current-company-name {
    font-size: 0.78em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.01em;
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .current-company-cnpj {
    ${mono};
    font-size: 0.62em;
    opacity: 0.45;
  }

  .current-company-pct {
    ${mono};
    font-size: 0.92em;
    font-weight: 500;
    color: ${tokens.brand};
    flex-shrink: 0;
    letter-spacing: -0.02em;
  }

  /* ============================================================
   * LAST DOWNLOAD TAG
   * ============================================================ */
  .last-download-tag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0 10px;
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    letter-spacing: 0.02em;

    svg { opacity: 0.6; font-size: 0.85em; }
  }

  /* ============================================================
   * PRIORITY QUEUE
   * ============================================================ */
  .priority-queue {
    padding: 0 16px 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .priority-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    margin: 0 -10px;
    border-radius: ${tokens.radiusSm};
    transition: background ${tokens.durBase} ${tokens.easeOut};
    opacity: 0.75;
    position: relative;

    &:hover {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 1)};
    }

    &.processing {
      opacity: 1;
      background: ${tokens.brandSoft};
    }

    /* Dot verde com glow no #1 (primeiro filho .processing) */
    &:first-child.processing::before {
      content: "";
      position: absolute;
      left: -2px;
      top: 50%;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: ${tokens.brand};
      box-shadow: 0 0 8px ${tokens.brandGlow};
      transform: translateY(-50%);
      animation: ${pulseDot} 2s ${tokens.easeInOut} infinite;
    }

    .priority-pos {
      ${mono};
      font-size: 0.68em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      min-width: 22px;
      text-align: center;
      letter-spacing: 0.02em;
    }

    .priority-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;

      .priority-name {
        font-size: 0.74em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.005em;
        text-transform: capitalize;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .priority-cnpj {
        ${mono};
        font-size: 0.6em;
        opacity: 0.45;
      }
    }

    .priority-nums {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;
      ${mono};
      font-size: 0.64em;

      .priority-total { opacity: 0.4; }

      .priority-done {
        color: ${tokens.brand};
        font-weight: 500;
      }

      .priority-pct {
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        min-width: 30px;
        text-align: right;
      }

      .priority-eta {
        font-size: 0.92em;
        opacity: 0.4;
        margin-left: 2px;
      }
    }
  }

  /* ============================================================
   * MONITOR STATS
   * ============================================================ */
  .monitor-stats {
    padding: 6px 16px 14px;
  }

  .monitor-stat-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 0;
    gap: 8px;

    &.highlight {
      padding: 6px 10px;
      margin: 2px -10px;
      border-radius: ${tokens.radiusSm};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .stat-label {
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      letter-spacing: -0.005em;

      svg { opacity: 0.6; }
    }

    .stat-value {
      ${mono};
      font-size: 0.78em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
      text-align: right;

      &.accent { color: ${tokens.brand}; }
      &.error-text { color: ${tokens.danger}; }
      &.muted {
        opacity: 0.5;
        font-size: 0.92em;
        word-break: break-all;
        max-width: 180px;
      }
    }
  }

  .monitor-stat-divider {
    height: 1px;
    background: ${({ theme }) => hairline(theme)};
    margin: 8px 0;
  }

  .monitor-progress-bar {
    height: 2px;
    background: ${({ theme }) => surface(theme, 2)};
    border-radius: ${tokens.radiusPill};
    overflow: hidden;
    margin: 8px 0 4px;
  }

  .monitor-progress-fill {
    height: 100%;
    background: ${tokens.brand};
    border-radius: ${tokens.radiusPill};
    transition: width 0.5s ${tokens.easeOut};
  }

  /* ============================================================
   * SERVICE INDICATORS (footer)
   * ============================================================ */
  .monitor-card-footer {
    padding: 10px 16px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
  }

  .service-indicators {
    display: flex;
    align-items: center;
    gap: 8px;
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;
    letter-spacing: 0.04em;

    span { margin-right: 4px; }
  }

  .service-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;

    &.online {
      background: ${tokens.brand};
      box-shadow: 0 0 6px ${tokens.brandGlow};
    }
    &.offline {
      background: ${({ theme }) => theme.text};
      opacity: 0.3;
    }
  }

  /* ============================================================
   * NODE CARD V2
   * ============================================================ */
  .node-v2-body { padding: 4px 16px 14px; }

  .node-v2-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
  }

  .node-status-badge {
    ${mono};
    font-size: 0.62em;
    font-weight: 500;
    padding: 3px 9px;
    border-radius: ${tokens.radiusPill};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid transparent;

    &.downloading {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border-color: ${tokens.brandRing};
    }
    &.idle {
      background: ${tokens.warnSoft};
      color: ${tokens.warn};
      border-color: rgba(245, 158, 11, 0.2);
    }
  }

  .node-current-key {
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.35;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .node-v2-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-bottom: 10px;
  }

  .node-metric {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    padding: 10px 6px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};
    transition: all ${tokens.durBase} ${tokens.easeOut};

    svg {
      opacity: 0.5;
      &.success { color: ${tokens.brand}; opacity: 0.8; }
      &.accent  { color: ${tokens.brand}; opacity: 0.7; }
      &.muted   { color: ${({ theme }) => theme.text}; opacity: 0.3; }
    }

    .metric-value {
      ${mono};
      font-size: 0.86em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;
      line-height: 1;
    }

    .metric-label {
      ${eyebrow};
      font-size: 0.55em;
      opacity: 0.4;
    }

    &:hover {
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
    }
  }

  .node-v2-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 10px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.5;

    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
  }

  .node-uptime { }

  .node-errors {
    color: ${tokens.danger} !important;
    opacity: 0.8 !important;
  }

  .node-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 24px 0;
    color: ${({ theme }) => theme.text};
    opacity: 0.3;
    font-size: 0.78em;
  }

  /* ============================================================
   * NODE DETAIL
   * ============================================================ */
  .node-detail {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .node-detail-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusSm};
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
    font-family: ${tokens.fontSans};
    font-size: 0.74em;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 12px;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    align-self: flex-start;

    &:hover {
      opacity: 1;
      background: ${({ theme }) => surface(theme, 1)};
      border-color: ${({ theme }) => hairline(theme, true)};
      transform: translateX(-2px);
    }
  }

  /* ============================================================
   * REQUEST CARD
   * ============================================================ */
  .download-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 14px 12px;
    cursor: pointer;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    overflow: hidden;

    &:hover {
      transform: translateY(-1px);
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};

      .cancel-btn-corner { opacity: 1; }
      .priority-dot { opacity: 0; }
    }

    /* Status badge */
    .status-badge {
      position: absolute;
      top: 10px;
      left: 10px;
      ${mono};
      font-size: 0.56em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      padding: 3px 8px;
      border-radius: ${tokens.radiusPill};
      border: 1px solid transparent;
      z-index: 2;

      &.pending {
        background: ${tokens.warnSoft};
        color: ${tokens.warn};
        border-color: rgba(245, 158, 11, 0.2);
      }
      &.processing {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
      }
      &.completed {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};

        /* Dot verde antes do texto pra cards completos */
        &::before {
          content: "";
          display: inline-block;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: ${tokens.brand};
          box-shadow: 0 0 4px ${tokens.brandGlow};
          margin-right: 5px;
          vertical-align: middle;
        }
      }
      &.cancelled, &.error {
        background: ${tokens.dangerSoft};
        color: ${tokens.danger};
        border-color: rgba(239, 68, 68, 0.2);
      }
    }

    /* Priority dot (substitui crown) */
    .priority-dot {
      position: absolute;
      top: 14px;
      right: 14px;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${tokens.brand};
      box-shadow: 0 0 10px ${tokens.brandGlow};
      animation: ${pulseDot} 2s ${tokens.easeInOut} infinite;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};
      z-index: 3;
    }

    /* Crown legacy (caso ainda exista no JSX, fallback compatível) */
    .crown-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      z-index: 3;
      svg {
        font-size: 0.85em;
        color: ${tokens.brand};
        opacity: 0.85;
        filter: drop-shadow(0 0 6px ${tokens.brandGlow});
      }
    }

    /* Cancel button minimal */
    .cancel-btn-corner {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 22px;
      height: 22px;
      background: transparent;
      border: none;
      border-radius: ${tokens.radiusSm};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      z-index: 4;

      svg {
        font-size: 0.85em;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
      }

      &:hover {
        background: ${tokens.dangerSoft};
        svg { color: ${tokens.danger}; opacity: 1; }
      }
    }

    /* Header */
    .download-header {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      margin-top: 18px;
      padding-bottom: 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .download-icon {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${tokens.brand};
        font-size: 0.95em;
        flex-shrink: 0;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        &.pending-icon { color: ${tokens.warn}; }
        &.processing-icon {
          color: ${tokens.brand};
          animation: ${pulseDot} 1.6s ${tokens.easeInOut} infinite;
        }
      }

      .download-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding-right: 28px;

        h5 {
          margin: 0;
          font-size: 0.84em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
          text-transform: capitalize;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .cnpj {
          ${mono};
          font-size: 0.66em;
          color: ${({ theme }) => theme.text};
          opacity: 0.45;
        }

        .project-tag {
          display: inline-block;
          ${mono};
          font-size: 0.6em;
          font-weight: 500;
          padding: 2px 7px;
          margin-top: 3px;
          border-radius: ${tokens.radiusPill};
          background: ${({ theme }) => surface(theme, 1)};
          color: ${({ theme }) => theme.text};
          opacity: 0.7;
          border: 1px solid ${({ theme }) => hairline(theme)};
          text-transform: uppercase;
          letter-spacing: 0.04em;
          max-width: 130px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    /* Progress */
    .download-progress-bar {
      position: relative;
      height: 2px;
      background: ${({ theme }) => surface(theme, 2)};
      border-radius: ${tokens.radiusPill};
      overflow: visible;
      margin: 4px 0 8px 0;

      .download-progress-fill {
        height: 100%;
        border-radius: ${tokens.radiusPill};
        background: ${tokens.brand};
        transition: width 0.4s ${tokens.easeOut};

        &.completed,
        &.processing,
        &.pending,
        &.error {
          background: ${tokens.brand};
        }

        &.pending { background: ${tokens.warn}; opacity: 0.7; }
        &.error   { background: ${tokens.danger}; }
      }

      .download-progress-text {
        position: absolute;
        top: -16px;
        right: 0;
        ${mono};
        font-size: 0.6em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        letter-spacing: -0.01em;
      }
    }

    /* Meta items */
    .download-meta {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;

      .meta-item {
        flex: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        padding: 6px 4px;
        background: ${({ theme }) => surface(theme, 0)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        min-width: 0;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg {
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          flex-shrink: 0;
        }

        .meta-value {
          ${mono};
          font-size: 0.7em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.85;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          letter-spacing: -0.01em;
        }

        &.success {
          svg { color: ${tokens.brand}; opacity: 0.85; }
          .meta-value { color: ${tokens.brand}; opacity: 1; }
        }

        &.error {
          svg { color: ${tokens.warn}; opacity: 0.75; }
          .meta-value { color: ${tokens.warn}; opacity: 0.95; }
        }
      }
    }

    /* Footer */
    .download-footer {
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      ${mono};
      font-size: 0.64em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      letter-spacing: 0.01em;

      svg { font-size: 0.95em; opacity: 0.7; }

      span {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }

      .percent-badge {
        font-size: 1em;
        font-weight: 500;
        padding: 3px 8px;
        border-radius: ${tokens.radiusPill};
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        color: ${({ theme }) => theme.text} !important;
        opacity: 0.85;
      }
    }
  }

  /* ============================================================
   * NEW REQUEST CARD (slot vazio "+")
   * ============================================================ */
  .download-card.new-request-card {
    background: transparent;
    border: 1px dashed ${({ theme }) => hairline(theme, true)};
    padding: 20px;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover {
      border-color: ${tokens.brandRing};
      background: ${tokens.brandSoft};
      transform: translateY(-1px);

      .new-request-content {
        opacity: 1;
        color: ${tokens.brand};
      }
    }

    .new-request-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      span {
        ${mono};
        font-size: 0.7em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4em 2em;
    text-align: center;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px dashed ${({ theme }) => hairline(theme, true)};
    border-radius: ${tokens.radiusLg};

    .empty-icon {
      width: 56px;
      height: 56px;
      border-radius: ${tokens.radiusMd};
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.2em;
      color: ${({ theme }) => theme.text};
      opacity: 0.4;

      svg { font-size: 1.5em; }
    }

    .empty-title {
      margin: 0 0 8px 0;
      font-size: 1em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.85;
      letter-spacing: -0.01em;
    }

    .empty-description {
      margin: 0;
      ${mono};
      font-size: 0.78em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      max-width: 420px;
      line-height: 1.5;
    }
  }

  /* ============================================================
   * LOADING
   * ============================================================ */
  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
    opacity: 0.5;
    gap: 10px;
    ${mono};
    font-size: 0.8em;
    color: ${({ theme }) => theme.text};
  }

  /* ============================================================
   * TERMINAL / LOGS (legacy - estilização sutil para compat)
   * ============================================================ */
  .terminal-card {
    border-radius: ${tokens.radiusLg};
    overflow: hidden;
  }

  .terminal-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 14px;
    background: ${({ theme }) => surface(theme, 1)};
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  }

  .terminal-dots {
    display: flex;
    gap: 5px;
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      &.red { background: #ff5f57; }
      &.yellow { background: #febc2e; }
      &.green { background: #28c840; }
    }
  }

  .terminal-title {
    display: flex;
    align-items: center;
    gap: 4px;
    ${mono};
    font-size: 0.66em;
    color: ${({ theme }) => theme.text};
    opacity: 0.45;
    letter-spacing: 0.02em;
  }

  .terminal-body {
    background: ${({ theme }) => theme.body === "#fafbfc" ? "#1e1e2e" : "#0d1017"};
    padding: 8px 12px;
    max-height: 260px;
    min-height: 140px;
    overflow-y: auto;
    ${mono};

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 2px; }
  }

  .terminal-line {
    display: flex;
    gap: 6px;
    padding: 1px 0;
    font-size: 0.66em;
    line-height: 1.5;

    &.success .terminal-msg { color: #4ade80; }
    &.error   .terminal-msg { color: #f87171; }
    &.warn    .terminal-msg { color: #fbbf24; }
    &.info    .terminal-msg { color: rgba(226, 232, 240, 0.7); }
  }

  .terminal-ts {
    color: rgba(107, 114, 128, 0.6);
    flex-shrink: 0;
    user-select: none;
  }

  .terminal-msg {
    color: #d1d5db;
    word-break: break-all;
  }

  .node-monitor-body {
    max-height: 400px !important;
    min-height: 240px !important;
    padding: 12px 14px !important;
  }

  .tm-section-header {
    ${mono};
    font-size: 0.62em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${tokens.brand};
    opacity: 0.7;
    text-align: center;
    padding: 2px 0 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    margin-bottom: 6px;
  }

  .tm-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2.5px 0;
    font-size: 0.68em;
  }

  .tm-label {
    color: rgba(156, 163, 175, 0.8);
    font-weight: 500;
  }

  .tm-value {
    ${mono};
    color: #e2e8f0;
    font-weight: 500;
    letter-spacing: -0.01em;

    &.tm-green { color: #4ade80; }
    &.tm-red { color: #f87171; }
    &.tm-yellow { color: #fbbf24; }
    &.tm-blue { color: #60a5fa; }
    &.tm-cyan { color: #22d3ee; }
    &.tm-dim {
      color: #6b7280;
      font-size: 0.9em;
      word-break: break-all;
      max-width: 180px;
      text-align: right;
    }
  }

  .tm-divider {
    ${mono};
    font-size: 0.6em;
    color: ${tokens.brand};
    text-align: center;
    padding: 6px 0 4px;
    letter-spacing: 0.08em;
    opacity: 0.4;
  }
`;
